import Box from './Box';
import Text from './Text';
import moment from 'moment';
import Button from './Button';
import '../components/Tab.css';
import TextInput from './TextInput';
import ErrorText from './ErrorText';
import { translate } from '../locale';
import LfaPopOver from './LfaPopOver';
import LocaleText from './LocaleText';
import React, { useState } from 'react';
import FormFieldGroup from './FormFieldGroup';
import { useBooking } from '../BookingProvider';
import courseApiClient from '../restApiClients/vsz/courseApiClient';
import courseService, { CourseType } from '../services/courseService';

export default function BookingFormModal(
  {
    type: urlType,
    courses,
    onSucceeded
  }
) {
  const { locale, org } = useBooking();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>();

  const [lfa, setLfa] = useState("");
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [street, setStreet] = useState("");
  const [postCode, setPostCode] = useState();
  const [city, setCity] = useState("");
  const [errors, setErrors] = useState<any>({});

  const type = courseService.mapUrlTypeToType(urlType);

  const isLocalBooking = (
    type === "FirstAid" ||
    type === "DrivingAttendant" ||
    type === "MotorcycleTraining"
  );

  const onSignup = async () => {
    const err = {};

    if (isLocalBooking) {
      if (!firstName) err['firstName'] = translate(locale, 'errorMessage', 'MissingFirstName');
      if (!lastName) err['lastName'] = translate(locale, 'errorMessage', 'MissingLastName');
      if (!street) err['street'] = translate(locale, 'errorMessage', 'MissingStreet');
      if (!postCode) err['postCode'] = translate(locale, 'errorMessage', 'MissingPostCode');
      if (!city) err['city'] = translate(locale, 'errorMessage', 'MissingCity');
    } else {
      if (!lfa) err['lfa'] = translate(locale, 'errorMessage', 'NoIdNumber');
    }

    let birthdate = moment([parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)]);
    if (!birthdate.isValid()) err['birthdate'] = translate(locale, 'errorMessage', 'MissingBirthdate');
    if (!email.match(/^\S+@\S+$/)) err['email'] = translate(locale, 'errorMessage', 'MissingMail');

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    const customer = {
      idNumber: lfa,
      birthdate: birthdate.format("YYYY-MM-DD"),
      email,
      language: locale,
      phoneNumber: phone
    };

    if (isLocalBooking) {
      Object.assign(customer, {
        student: {
          firstName,
          lastName,
          birthdate: customer.birthdate,
          email: customer.email,
          phoneNumber: customer.phoneNumber,
          address: {
            street,
            postCode,
            city
          }
        }
      });
    }

    try {
      setIsLoading(true);

      if (
        courses.length === 1 &&
        courses[0].type === 'PGS'
      ) {
        await courseApiClient.bookVKU({
          ...customer,
          courseGroupId: courses[0].groupId
        });
      } else {
        for (const course of courses) {
          const data = {
            ...customer,
            courseId: course._id
          };

          if (isLocalBooking) {
            await courseApiClient.book(type, data);
          } else {
            await courseApiClient.bookVKU(data);
          }
        }
      }
    } catch (err) {
      setError(err);
      return;
    } finally {
      setIsLoading(false);
    }

    try {
      reportConversion(type, org);
    } catch (err) {
      console.error(err);
    }

    if (onSucceeded) onSucceeded();
  };

  return (
    <Box>
      <Box marginTop={16}>
        <Text size={20}>{translate(locale, 'infoMessage', 'enterInfos')}</Text>
        {
          isLocalBooking &&
          <>
            <FormFieldGroup label={translate(locale, 'infoMessage', 'firstName')}>
              <TextInput placeholder='Max' value={firstName} onValueChange={(value) => setFirstName(value)} />
              {errors.firstName && <ErrorText error={errors.firstName} />}
            </FormFieldGroup>

            <FormFieldGroup label={translate(locale, 'infoMessage', 'lastName')}>
              <TextInput placeholder='Muster' value={lastName} onValueChange={(value) => setLastName(value)} />
              {errors.lastName && <ErrorText error={errors.lastName} />}
            </FormFieldGroup>
          </>
        }

        {
          !isLocalBooking &&
          <FormFieldGroup label={translate(locale, 'infoMessage', 'learnersPermitNumber')}>
            <Box direction='row' alignItems='center'>
              <TextInput placeholder='0006.091.669' value={lfa} onValueChange={(value) => setLfa(value)} />
              {errors.lfa && <ErrorText error={errors.lfa} />}
              <Box paddingLeft={8}>
                <LfaPopOver />
              </Box>
            </Box>
          </FormFieldGroup>
        }

        <FormFieldGroup label={translate(locale, 'infoMessage', 'birthdate')}>
          <Box direction='row' alignItems='center'>
            <TextInput placeholder='1' type='number' value={day} onValueChange={(value) => setDay(value)} />
            <TextInput placeholder='10' type='number' value={month} onValueChange={(value) => setMonth(value)} />
            <TextInput placeholder='1995' type='number' value={year} onValueChange={(value) => setYear(value)} />
          </Box>
          {errors.birthdate && <ErrorText error={errors.birthdate} />}
        </FormFieldGroup>

        {
          isLocalBooking &&
          <>
            <FormFieldGroup label={translate(locale, 'infoMessage', 'street')}>
              <TextInput placeholder='Hauptstrasse 1' value={street} onValueChange={(value) => setStreet(value)} />
              {errors.street && <ErrorText error={errors.street} />}
            </FormFieldGroup>

            <Box direction='row' marginVertical={12} >
              <FormFieldGroup label={translate(locale, 'infoMessage', 'postCode')} marginVertical={0}>
                <TextInput placeholder='8000' value={postCode} onValueChange={(value) => setPostCode(value)} />
                {errors.postCode && <ErrorText error={errors.postCode} />}
              </FormFieldGroup>

              <FormFieldGroup label={translate(locale, 'infoMessage', 'city')} marginVertical={0}>
                <TextInput placeholder='Zürich' value={city} onValueChange={(value) => setCity(value)} />
                {errors.city && <ErrorText error={errors.city} />}
              </FormFieldGroup>
            </Box>
          </>
        }

        <FormFieldGroup label={translate(locale, 'infoMessage', 'phoneNumber')}>
          <TextInput placeholder='079 123 45 67' type='tel' value={phone} onValueChange={(value) => setPhone(value)} />
          {errors.phone && <ErrorText error={errors.phone} />}
        </FormFieldGroup>

        <FormFieldGroup label={translate(locale, 'infoMessage', 'email')}>
          <TextInput placeholder='info@verkehrsschule.ch' type='email' value={email} onValueChange={(value) => setEmail(value)} />
          {errors.email && <ErrorText error={errors.email} />}
        </FormFieldGroup>

        <Box marginTop={16}>
          <Text>{translate(locale, 'infoMessage', 'byRegistering')} <a target="_blank" rel="noopener noreferrer" href={locale === 'de' ? 'https://www.verkehrsschule.ch/allgemeine-geschaeftsbedingungen/' : 'https://www.verkehrsschule.ch/en/general-terms-and-conditions/'}><strong>{translate(locale, 'infoMessage', 'termsOfService')}</strong></a>.</Text>
        </Box>

        {
          error &&
          <Box marginTop={16}>
            <Text color='red'>
              <LocaleText
                language={locale}
                keyName='errorMessage'
                value={error?.response?.data?.code ?? error?.code ?? 'InternalServerError'}
              />
            </Text>
          </Box>
        }

        <Box marginTop={16}>
          <Button
            pending={isLoading}
            onClick={onSignup}
            label={translate(locale, 'infoMessage', 'signUp')}
          />
        </Box>
      </Box>
    </Box>
  );
}

const CONVERSIONS = {
  FirstAid: {
    google_conversion_id: 869367969,
    google_conversion_language: 'en',
    google_conversion_color: 'ffffff',
    google_conversion_label: '7XnUCIaAn_UBEKGBxp4D',
    google_conversion_format: '3',
    google_remarketing_only: false
  },
  PGS: {
    google_conversion_id: 869367969,
    google_conversion_language: 'en',
    google_conversion_color: 'ffffff',
    google_conversion_label: 'IP-iCMOuwfUBEKGBxp4D',
    google_conversion_format: '3',
    google_remarketing_only: false
  },
  VKU: {
    google_conversion_id: 869367969,
    google_conversion_language: 'en',
    google_conversion_color: 'ffffff',
    google_conversion_label: 'xpkQCJqzoPUBEKGBxp4D',
    google_conversion_format: '3',
    google_remarketing_only: false
  }
};

function reportConversion(type: CourseType, org?) {
  console.log("REPORT CONVERSION", type, org, ">");

  if (org) {
    (window as any).gtag('event', 'conversion', {
      'send_to': 'AW-10899017175/O1K6CNSlgcQDENebh80o',
      'transaction_id': ''
    });
  } else if (CONVERSIONS[type]) {
    const conversion = CONVERSIONS[type];

    Object
      .keys(conversion)
      .forEach((prop) => {
        window[prop] = conversion[prop];
      });

    (window as any).google_trackConversion({
      onload_callback: () => { }
    });
  }
}
